import { FC, useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { useRouteMatch } from 'react-router-dom';
import cn from 'clsx';

import ButtonMighty from '@components/elements/ButtonMighty';
import InputText from '@components/elements/InputText';
import PollQuestionCard from '@components/modules/Poll/QuestionCard';
import Logo from '@images/logo.png';

import useSwr, { mutate } from 'swr';
import { proxyCreateUser } from '@sdk/managers/UsersManager';
import {
  proxyVotePoll,
  proxyWithdrawPollAll,
} from '@sdk/managers/VotesManager';
import { fetchPollDetails } from '@utils/Poll';
import { useAuth } from '@hooks/Auth';

import HeaderImage from '@components/elements/HeaderImage';
import { Colors } from '@components/elements/Colors';
import Dialog from '@components/elements/Dialog';

import ProxyVotes from '@components/modules/Poll/ProxyVotes';
import PledgeCheckoutForm from '@components/modules/Poll/PledgeCheckoutForm';
import { ProxyVote } from '@utils/Proxy';
import { isWithinInterval } from 'date-fns';
import { useMemo } from 'react';
import { PollStatus } from '@sdk/models';

const ProxyVoteButton: FC = () => {
  const {
    params: { pollId },
  } = useRouteMatch<{
    pollId: string;
  }>();

  const { dbUser, company, loading, user } = useAuth();

  const [modal, setModal] = useState<boolean>(false);
  const hideModal = useCallback(() => setModal(false), [setModal]);
  const showModal = useCallback(() => setModal(true), [setModal]);

  const { data, error } = useSwr(
    pollId && loading === false
      ? ['/poll/detail', pollId, user === null]
      : null,
    fetchPollDetails,
    {
      revalidateOnFocus: true,
    }
  );

  const poll = data?.poll;

  const [selectedProxy, setSelectedProxy] = useState<ProxyVote>();
  const [email, setEmail] = useState<string>();
  const [name, setName] = useState<string>();
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [submitting, setSubmitting] = useState<boolean>(false);

  const isPledgePeriod = useMemo(() => {
    if (!poll) return false;
    if (!data.poll.pledgePeriod1StartDate || !data.poll.pledgePeriod1EndDate)
      return false;
    return isWithinInterval(new Date(), {
      start: data.poll.pledgePeriod1StartDate,
      end: data.poll.pledgePeriod1EndDate,
    });
  }, [data, poll]);
  const isVotePeriod = useMemo(() => {
    if (!poll) return true;
    if (!data.poll.startDate || !data.poll.endDate) return false;
    return isWithinInterval(new Date(), {
      start: data.poll.startDate,
      end: data.poll.endDate,
    });
  }, [data, poll]);

  const questionsCount = (data?.subPolls?.length || 0) + 1;
  let formErrors: Record<string, string> = {};
  if (isVotePeriod && Object.keys(selected).length < questionsCount)
    formErrors.selected = 'Select all options';
  if (name === undefined || name === '') formErrors.name = 'Empty name';
  if (email === undefined || email === '') formErrors.email = 'Empty email';

  const onSubmit = useCallback(async () => {
    try {
      setSubmitting(true);
      if (!dbUser?.id || !dbUser?.email || !data)
        throw new Error('Unexpected error.');
      if (!company) throw new Error('Failed to get company.');
      if (!email) throw new Error('Invalid email.');

      const res = await toast.promise(
        (async () => {
          if (Object.keys(selected).length < questionsCount) {
            throw new Error('You need to select responses for all questions.');
          }

          const proxyUser = await toast.promise(
            proxyCreateUser(dbUser.id, {
              email: email,
              username: email,
              firstName: name,
              company: 'testcompany',
              proxyUserID: dbUser.id,
            }) as Promise<{
              data?: {
                result: {
                  id: string;
                  owner: string;
                  username: string;
                };
              };
            }>,
            {
              loading: 'Creating Proxy user...',
              success: 'Created Proxy user',
              error: (err) => {
                console.log('Failed to create proxy user', err);
                return (
                  err.error || err.message || 'Failed to create proxy user'
                );
              },
            }
          );

          const proxyId = proxyUser.data?.result.id;
          if (!proxyId) throw new Error('Unexpected error creating proxy user');

          const subPollObj =
            data.subPolls?.reduce((r, a) => {
              r[a.id] = a;
              return r;
            }, {}) || {};

          if (
            !data.poll.isNotExact &&
            (!selected[data.poll.id] ||
              selected[data.poll.id]?.length <
                (!data.poll.maxVote || data.poll.maxVote < 1
                  ? 1
                  : data.poll.maxVote))
          ) {
            toast.error(
              `You have to vote ${
                !data.poll.maxVote || data.poll.maxVote < 1
                  ? 1
                  : data.poll.maxVote
              } options for ${data.poll.question} question`
            );
            return;
          }
          const notYet = Object.keys(selected)
            .filter((key) => key !== data.poll.id)
            .find(
              (key) =>
                !selected[key] ||
                selected[key]?.length <
                  (!subPollObj[key]?.maxVote || subPollObj[key]?.maxVote < 1
                    ? 1
                    : subPollObj[key]?.maxVote)
            );
          if (!!notYet && !subPollObj[notYet]?.isNotExact) {
            console.log('nenene');
            toast.error(
              `You have to vote ${
                !subPollObj[notYet]?.maxVote || subPollObj[notYet]?.maxVote < 1
                  ? 1
                  : subPollObj[notYet]?.maxVote
              } options for ${subPollObj[notYet]?.question} question`
            );
            return;
          }

          await proxyWithdrawPollAll(dbUser?.id, data.poll.id, null);

          await Promise.all(
            Object.keys(selected).map((optionId) => {
              selected[optionId].map((item) => {
                return proxyVotePoll(
                  dbUser.id,
                  proxyId,
                  item,
                  data.poll.id,
                  optionId === data.poll.id ? undefined : optionId
                );
              });
              // return submitVote(dbUser.id, selected[optionId], poll.id, {
              //   subPollId: optionId === poll.id ? undefined : optionId,
              //   edit: voted,
              // });
            })
          );

          // const votesRes = await Promise.all(
          //   Object.keys(selected).map((optionId) => {
          //     return proxyVotePoll(
          //       dbUser.id,
          //       proxyId,
          //       selected[optionId],
          //       data.poll.id,
          //       optionId === data.poll.id ? undefined : optionId
          //     );
          //   })
          // );

          await mutate(['/poll/proxyVotes', pollId, dbUser.id]).catch((err) =>
            console.log('mutate', err)
          );
          return proxyUser;
        })(),
        {
          loading: 'Casting proxy vote',
          success: 'Successfully casted proxy vote',
          error: (err) =>
            err.error || err.message || 'Failed to add proxy vote',
        }
      );
      console.log(res);
      hideModal();
    } catch (err: any) {
      console.log('Proxy vote unexpected error', err);
      toast.error(err.message);
    } finally {
      setEmail(undefined);
      setName(undefined);
      setSubmitting(false);
    }
  }, [
    dbUser,
    data,
    company,
    email,
    hideModal,
    selected,
    questionsCount,
    name,
    pollId,
  ]);

  if (error?.code === 'NotFound')
    return (
      <div className="pt-32">
        <div className="flex flex-col items-center justify-center px-4 py-24 mx-4 text-center bg-white shadow-r-salmon-md">
          <h1 className="text-3xl sm:text-4xl">Poll is no longer available.</h1>
          <p>The poll has been deleted or this is an invalid poll.</p>

          <div className="mt-24">
            <img src={Logo} alt="migthypoll" />
          </div>
        </div>
      </div>
    );

  const currencyFormatter = new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
  });

  return (
    <>
      <Dialog
        className="text-center text-black bg-yellow"
        title="Proxy User"
        content={
          <div className="flex flex-col space-y-2">
            <p className="text-sm">Enter email here</p>
            <InputText
              name="email"
              type="email"
              placeholder="Email"
              onChange={(value) => setEmail(value)}
              value={email || ''}
              borderColor={
                email !== undefined && formErrors.email ? Colors.red : undefined
              }
              shadowColor={
                email !== undefined && formErrors.email
                  ? Colors.yellow
                  : undefined
              }
            />
            <p className="mb-4 text-sm">Enter name here</p>
            <InputText
              name="firstName"
              type="text"
              placeholder="Name"
              onChange={(value) => setName(value)}
              value={name || ''}
              borderColor={
                name !== undefined && formErrors.name ? Colors.red : undefined
              }
              shadowColor={
                name !== undefined && formErrors.name
                  ? Colors.yellow
                  : undefined
              }
            />
          </div>
        }
        open={modal}
        onCancel={hideModal}
        onConfirm={onSubmit}
      />
      <div className="h-screen overflow-x-hidden overflow-y-scroll bg-fixed bg-gray-darkest poll-x-pattern">
        <HeaderImage
          title={poll?.title || ''}
          titleSize={(poll?.title?.length || 0) > 15 ? 40 : 55}
          image={poll?.image?.url || 'image'}
          alt="poll picture"
        />
        <div className="flex flex-col gap-4 mx-auto lg:container xl:gap-8 lg:flex-row">
          <div className="w-full lg:min-h-screen lg:sticky lg:top-0 lg:w-108">
            <div className="flex flex-col mx-4 my-4 space-y-4 lg:mr-0 lg:ml-4">
              <div className="px-4 py-4 space-y-2 text-white bg-black">
                <h2 className="mb-2 text-2xl text-white">Proxy Votes</h2>
                <ProxyVotes
                  pollId={pollId}
                  active={selectedProxy?.id}
                  onItemClick={(proxy, withdrew) => {
                    if (proxy.id === selectedProxy?.id)
                      setSelectedProxy(undefined);
                    else if (!withdrew) setSelectedProxy(proxy);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-1">
            <div className="flex-1 pb-24">
              <div className="flex flex-col mx-4 my-4 space-y-4 lg:mr-4 lg:ml-0">
                {isPledgePeriod ? (
                  poll?.status === PollStatus.SWING ? (
                    <div className="px-4 pt-4 pb-8 space-y-4 text-white bg-black">
                      <h3 className="mb-2 text-lg text-yellow">
                        THERE IS A TIE!
                      </h3>
                    </div>
                  ) : (
                    poll && (
                      <div className="px-4 pt-4 pb-8 space-y-4 text-white bg-black">
                        <h3 className="mb-2 text-lg text-yellow">WINNER</h3>

                        {poll.winOption?.detail?.image?.url && (
                          <div className="mb-4">
                            <img
                              src={poll.winOption.detail.image.url}
                              alt="winner"
                            />
                          </div>
                        )}

                        <div
                          key={`winner-${poll.winOption?.id}`}
                          className={cn('flex flex-col w-full')}
                        >
                          <h3
                            className={cn('text-2xl', {
                              'mb-4': poll.winOption?.description !== undefined,
                            })}
                          >
                            {poll.winOption?.title}
                          </h3>

                          {poll.winOption?.description && (
                            <p
                              className={cn({
                                'text-base':
                                  poll.winOption?.description.length <= 110,
                                'text-sm':
                                  poll.winOption?.description.length > 110,
                              })}
                            >
                              {poll.winOption?.description}
                            </p>
                          )}
                          {poll.winOption?.detail?.price && (
                            <p>
                              {poll.winOption?.detail?.price &&
                                parseFloat(poll.winOption?.detail?.price) &&
                                currencyFormatter.format(
                                  parseFloat(poll.winOption?.detail?.price)
                                )}
                            </p>
                          )}
                        </div>
                        <div className="pt-6">
                          <PledgeCheckoutForm
                            pollId={pollId}
                            userId={selectedProxy?.userID}
                            proxy
                          />
                        </div>
                      </div>
                    )
                  )
                ) : (
                  <div className="px-4 pt-4 pb-8 space-y-4 bg-black">
                    {poll && (
                      <PollQuestionCard
                        index={1}
                        poll={poll}
                        vote={selected[poll.id]}
                        onSelect={(id: string, type = 'select') => {
                          const maxVote =
                            !poll.maxVote || poll.maxVote < 1
                              ? 1
                              : poll.maxVote;
                          if (type === 'select') {
                            if (maxVote === 1) {
                              setSelected({
                                ...selected,
                                [poll.id]: [id],
                              });
                            } else if (
                              !selected[poll.id] ||
                              Object.keys(selected).length === 0 ||
                              selected[poll.id].length < maxVote
                            )
                              setSelected({
                                ...selected,
                                [poll.id]: [...(selected[poll.id] || []), id],
                              });
                          } else {
                            setSelected({
                              ...selected,
                              [poll.id]: selected[poll.id].filter(
                                (item) => item !== id
                              ),
                            });
                          }
                        }}
                      />
                    )}
                    {poll &&
                      data?.subPolls
                        ?.filter((subPoll) => !!subPoll.id)
                        .map((subPoll, index) => (
                          <PollQuestionCard
                            key={`subpoll-${subPoll.id}`}
                            index={index + 2}
                            poll={poll}
                            subPoll={subPoll}
                            vote={selected[subPoll.id]}
                            onSelect={(id: string, type = 'select') => {
                              const maxVote =
                                !subPoll.maxVote || subPoll.maxVote < 1
                                  ? 1
                                  : subPoll.maxVote;
                              if (type === 'select') {
                                if (maxVote === 1) {
                                  setSelected({
                                    ...selected,
                                    [subPoll.id]: [id],
                                  });
                                } else if (
                                  !selected[subPoll.id] ||
                                  Object.keys(selected).length === 0 ||
                                  selected[subPoll.id].length < maxVote
                                )
                                  setSelected({
                                    ...selected,
                                    [subPoll.id]: [
                                      ...(selected[subPoll.id] || []),
                                      id,
                                    ],
                                  });
                              } else {
                                setSelected({
                                  ...selected,
                                  [subPoll.id]: selected[subPoll.id].filter(
                                    (item) => item !== id
                                  ),
                                });
                              }
                            }}
                          />
                        ))}
                    <div
                      className={cn('w-full xl:w-auto', {
                        'opacity-60': !!formErrors.selected,
                      })}
                    >
                      <ButtonMighty
                        disabled={submitting || !!formErrors.selected}
                        loading={submitting}
                        color="#FFFF00"
                        borderColor="#ff1dfc"
                        textColor="#5600fb"
                        text="Cast Proxy Vote"
                        margin="0"
                        onClick={showModal}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProxyVoteButton;
